import { config, defaultOidcClients } from '../config';
import { SyberKeyIdpSdk } from '../sdk/syberkeyIdpSdk';

// API Service runs on localhost:3000 or 3002
// Auth Portal runs on localhost:3003
// MaxLife runs on localhost:3001

/**
 * Get OIDC base URL based on environment
 * Development: CRA `npm start` (NODE_ENV=development), localhost, or *.localhost — uses
 *   REACT_APP_OIDC_BASE_URL or config.json `oidc.baseUrl` (default http://localhost:3000).
 * Production builds: https://api.syberkey.com (e.g. opened from sso.syberkey.com or a non-local host).
 */
function getOidcBaseUrl(): string {
  const hostname = window.location.hostname;
  // CRA `npm start` sets NODE_ENV=development even when you open the app via LAN IP
  // (e.g. http://10.x.x.x:3001). Hostname-only checks wrongly treated that as "production"
  // and sent discovery to https://api.syberkey.com.
  const isDevelopment =
    process.env.NODE_ENV === 'development' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('localhost');

  console.log('[OIDC] Hostname check:', {
    hostname,
    nodeEnv: process.env.NODE_ENV,
    isDevelopment
  });

  if (isDevelopment) {
    // Development: env override, else config.json default (localhost syberkey-service)
    const devUrl = config.oidc.baseUrl;
    console.log('[OIDC] Development mode detected. Using OIDC base URL:', devUrl);
    return devUrl;
  }
  
  // Production: Always use production API, ignore any build-time env vars
  const prodUrl = 'https://api.syberkey.com';
  console.log('[OIDC] Production mode detected. Using OIDC base URL:', prodUrl);
  return prodUrl;
}

// Get OIDC base URL dynamically at runtime
const OIDC_BASE_URL = getOidcBaseUrl();
console.log('[OIDC] OIDC_BASE_URL initialized:', OIDC_BASE_URL, '(hostname:', window.location.hostname + ')');
const AUTH_PORTAL_URL = config.authPortal.url;
const idpSdk = new SyberKeyIdpSdk(OIDC_BASE_URL);

/**
 * Get OIDC client configuration based on tenant
 * Each tenant should have its own client ID/secret configured via environment variables
 * 
 * Default client IDs are pre-registered in the backend:
 * - maxlife_26cc36 -> maxlife-dev-client-12345 (pre-registered in backend)
 */
function getOidcClientConfig(tenantId: string): { clientId: string; clientSecret: string; redirectUri: string } {
  // Get default client config for this tenant
  const defaultClient = defaultOidcClients[tenantId];
  
  // Use config helper functions to get client ID and secret with fallback chain
  const clientId = config.oidcClient.getClientId(tenantId, defaultClient?.clientId);
  const clientSecret = config.oidcClient.getClientSecret(tenantId, defaultClient?.clientSecret);
  
  return {
    clientId,
    clientSecret,
    redirectUri: config.oidc.getRedirectUri()
  };
}

/**
 * Get OIDC client configuration (tenant-aware, no dynamic registration)
 */
export async function ensureOidcClient(): Promise<{ clientId: string; clientSecret: string }> {
  // Get tenant ID from config
  const tenantId = config.tenant.id;
  
  const clientConfig = getOidcClientConfig(tenantId);
  
  console.log(`[OIDC] Using client config for tenant: ${tenantId}, clientId: ${clientConfig.clientId}`);
  
  return {
    clientId: clientConfig.clientId,
    clientSecret: clientConfig.clientSecret
  };
}

/**
 * Initiate OIDC authorization flow
 */
export async function initiateOidcAuth(): Promise<void> {
  try {
    // Ensure client is registered
    const { clientId } = await ensureOidcClient();

    // Validate clientId
    if (!clientId || clientId === 'undefined') {
      console.error('[OIDC] Invalid clientId:', clientId);
      throw new Error('OIDC client ID is missing. Please check client registration.');
    }

    console.log('[OIDC] Using clientId:', clientId);

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store code verifier for later use
    sessionStorage.setItem('oidc_code_verifier', codeVerifier);

    // Generate state for CSRF protection
    const state = generateRandomString(32);
    sessionStorage.setItem('oidc_state', state);

    // Build authorization URL
    // Include custom parameters for clientName and source that will be passed to Auth Portal
    const clientName = config.app.clientName;
    const source = config.app.source;
    
    const redirectUri = config.oidc.getRedirectUri();
    const authUrl = await idpSdk.buildAuthorizationUrl({
      clientId,
      redirectUri,
      scope: 'openid profile email offline_access',
      state,
      codeChallenge,
      clientName,
      source
    });
    
    console.log('[OIDC] Initiating authorization flow:', authUrl);
    console.log('[OIDC] Client ID in URL:', clientId);
    
    window.location.href = authUrl;
  } catch (error) {
    console.error('[OIDC] Error initiating auth:', error);
    throw error;
  }
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(code: string, state: string): Promise<{
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type?: string;
  scope?: string;
  user?: {
    userId: string;
    email: string;
    fullName: string;
    preferred_username?: string;
  };
}> {
  try {
    // Verify state
    const storedState = sessionStorage.getItem('oidc_state');
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter');
    }

    // Get code verifier
    const codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    // Get client config (hardcoded)
    const clientConfig = await ensureOidcClient();

    const redirectUri = config.oidc.getRedirectUri();
    console.log('[OIDC] Token exchange request via SDK:', {
      code: code.substring(0, 20) + '...',
      redirect_uri: redirectUri,
      client_id: clientConfig.clientId,
      has_code_verifier: !!codeVerifier
    });

    // Exchange code for tokens via SDK/discovery endpoints
    const tokenResponse = await idpSdk.exchangeCodeForTokens({
      code,
      redirectUri,
      codeVerifier,
      clientId: clientConfig.clientId,
      clientSecret: clientConfig.clientSecret
    });

    console.log('[OIDC] ✅ Token exchange successful:', {
      has_access_token: !!tokenResponse.access_token,
      access_token_preview: tokenResponse.access_token ? tokenResponse.access_token.substring(0, 30) + '...' : 'none',
      has_id_token: !!tokenResponse.id_token,
      has_refresh_token: !!tokenResponse.refresh_token,
      expires_in: tokenResponse.expires_in,
      token_type: tokenResponse.token_type,
      scope: tokenResponse.scope,
      has_user_info: !!tokenResponse.user
    });

    // Log user info if present in token response (backend adds this)
    if (tokenResponse.user) {
      console.log('[OIDC] 👤 User info in token response:', {
        userId: tokenResponse.user.userId,
        email: tokenResponse.user.email,
        fullName: tokenResponse.user.fullName
      });
    }

    // Clear PKCE data
    sessionStorage.removeItem('oidc_code_verifier');
    sessionStorage.removeItem('oidc_state');

    return tokenResponse;
  } catch (error: any) {
    console.error('[OIDC] ❌ Error exchanging code for tokens:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      error: error.response?.data?.error,
      error_description: error.response?.data?.error_description,
      error_uri: error.response?.data?.error_uri,
      response_data: error.response?.data,
      request_config: {
        url: error.config?.url,
        method: error.config?.method,
        has_auth_header: !!error.config?.headers?.Authorization
      }
    });

    // Provide more detailed error message
    let errorMessage = 'Failed to exchange authorization code';
    if (error.response?.data?.error_description) {
      errorMessage = error.response.data.error_description;
    } else if (error.response?.data?.error) {
      errorMessage = `Token exchange failed: ${error.response.data.error}`;
    } else if (error.message) {
      errorMessage = `Token exchange failed: ${error.message}`;
    }

    throw new Error(errorMessage);
  }
}

/**
 * Get user info from access token
 */
export async function getUserInfo(accessToken: string): Promise<any> {
  try {
    console.log('[OIDC] 🔍 Requesting user info via SDK...');
    console.log('[OIDC] Access token preview:', accessToken.substring(0, 30) + '...');
    const response = await idpSdk.getUserInfo(accessToken);

    console.log('[OIDC] ✅ User info response received:', {
      has_sub: !!response.sub,
      has_email: !!response.email,
      has_name: !!response.name,
      has_preferred_username: !!response.preferred_username,
      claims: Object.keys(response),
      full_response: response
    });

    // Validate response format
    if (!response.sub) {
      console.warn('[OIDC] ⚠️ User info response missing "sub" claim');
    }

    // Ensure we have at least email or name
    if (!response.email && !response.name) {
      console.warn('[OIDC] ⚠️ User info response missing both email and name');
    }

    return response;
  } catch (error: any) {
    console.error('[OIDC] ❌ Error getting user info:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      error: error.response?.data?.error,
      error_description: error.response?.data?.error_description,
      response_data: error.response?.data
    });

    // Provide more detailed error message
    let errorMessage = 'Failed to get user information';
    if (error.response?.data?.error_description) {
      errorMessage = error.response.data.error_description;
    } else if (error.response?.data?.error) {
      errorMessage = `Failed to get user information: ${error.response.data.error}`;
    } else if (error.message) {
      errorMessage = `Failed to get user information: ${error.message}`;
    }

    throw new Error(errorMessage);
  }
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(refreshToken: string): Promise<{
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}> {
  try {
    console.log('[OIDC] 🔄 Refreshing access token...');
    const clientConfig = await ensureOidcClient();

    const response = await idpSdk.refreshAccessToken({
      refreshToken,
      clientId: clientConfig.clientId,
      clientSecret: clientConfig.clientSecret
    });

    console.log('[OIDC] ✅ Token refresh successful:', {
      has_access_token: !!response.access_token,
      has_id_token: !!response.id_token,
      has_refresh_token: !!response.refresh_token,
      expires_in: response.expires_in
    });

    return response;
  } catch (error: any) {
    console.error('[OIDC] ❌ Error refreshing token:', {
      message: error.message,
      status: error.response?.status,
      error: error.response?.data?.error,
      error_description: error.response?.data?.error_description
    });

    let errorMessage = 'Failed to refresh access token';
    if (error.response?.data?.error_description) {
      errorMessage = error.response.data.error_description;
    } else if (error.response?.data?.error) {
      errorMessage = `Token refresh failed: ${error.response.data.error}`;
    }

    throw new Error(errorMessage);
  }
}

/**
 * Generate PKCE code verifier
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate PKCE code challenge from verifier
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64 URL encode
 */
function base64UrlEncode(array: Uint8Array): string {
  // Convert Uint8Array to regular array to avoid spread operator issues
  const charCodes = Array.from(array);
  return btoa(String.fromCharCode(...charCodes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate random string
 */
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export { OIDC_BASE_URL, AUTH_PORTAL_URL };

